<template>
    <section class="test-drive">
        <div class="select-datetime__container container">
            <StepBar :step="4" step-name="Select a date and time" />
            <Navigation :go-to-step="{ name: 'select-dealer', params: {brand: brand, urlModel: this.urlModel}}" />

            <div class="test-drive__content">
                <div class="select-datetime__content--main">
                    <h2 class="title">Select a date & time</h2>

                    <p class="description">Thank you for selecting {{ dealer.name }}. Please select the preferred date and time for your test drive.</p>

                    <div class="card">
                        <div class="card__inner">
                            <div class="select-datetime__content--form">
                                <div class="select-datetime__content--input">
                                    <div class="select-datetime__content--flatpickr">
                                        <input type="text" name="date" ref="date" id="date" class="form-control form-control--select select-datetime__content--select-input" :value="formattedDate" placeholder="Please select a date" />
                                        <button class="select-datetime__content--button-input" type="button" @click="toggleDateSelect"></button>
                                    </div>
                                </div>

                                <div class="select-datetime__content--dealer">
                                    <h3 class="select-datetime__content--dealer-title">Dealer Address</h3>
                                    <p class="select-datetime__content--dealer-info">
                                        {{ dealer.name }} <br />
                                        {{ dealer.address }} <br />
                                        {{ dealer.postcode }}
                                    </p>
                                </div>
                            </div>

                            <div v-if="dayTimes" class="select-datetime__content--options">
                                <div v-for="(time, index) in dayTimes" :key="index" class="select-datetime__content--option" :class="{ active: selectedTime === time }" @click="updateTime(time)">
                                    <input type="radio" :id="time.split(' ').join('')" name="time" :value="time.split(' ').join('')" class="select-datetime__content--radio" />
                                    <label :for="time.split(' ').join('')">{{ time }}</label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <router-link :to="{ name: 'your-details', params: { brand: this.brand, urlModel: this.urlModel } }" class="btn btn--primary select-datetime__content--continue" :class="{ disabled: !storeTime || !date }">Continue</router-link>
                </div>

                <div class="select-datetime__content--info">
                    <picture v-if="model.image">
                        <source v-if="model.image_webp" :srcset="model.image_webp" type="image/webp" />
                        <img class="img-responsive select-datetime__content--image" :src="model.image" :alt="model.model_title" />
                    </picture>

                    <div class="select-datetime__content--details">
                        <h3 class="capitalise">{{ model.model_title }}</h3>
                        <p class="extras">
                            <span>Engine:</span> {{ fuelChoice }} <br />
                            <span>Transmission:</span> {{ transmissionChoice.toLowerCase() }}
                        </p>

                        <p v-if="notes !== ''">
                            <span>Notes:</span> <br />
                            {{ notes }}
                        </p>
                    </div>

                    <div class="select-datetime__content--fix-spacing">
                        <Navigation :go-to-step="{ name: 'select-model', params: {brand: brand}}" link-text="Change model" />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import StepBar from "../components/StepBar";
import Navigation from "../components/Navigation";
import { mapState } from "vuex";
import EventObject from "../../modules/tracking/EventObject";
import { addDays, addMonths } from 'date-fns';
const isSameDay = require('date-fns/is_same_day')


export default {
    props: {
        brand: {
            required: true,
        },
        urlModel: {
            required: true,
        },
    },
    components: {
        StepBar,
        Navigation,
    },
    data() {
        return {
            chosenDate:null,
            minDate: null,
            maxDate: null,
            flatpickrInstance: false,
            dayTimes: null,
            selectedTime: null,
            closedDays: [],
        };
    },

    created() {
        if (!this.haveMinimumModelData) {
            this.$router.push({ name: 'select-options', params: {brand: brand, urlModel: this.urlModel}});
            return;
        }
        if (!this.haveMinimumDealerData) {
            this.$router.push({ name: 'select-dealer', params: {brand: brand, urlModel: this.urlModel}});
            return;
        }
    },

    mounted() {
        this.minDate = addDays(new Date(), 2),
        this.maxDate = addMonths(this.minDate, 3);
        this.getDealerClosedDays()
            .then((data) => {
                this.$nextTick(() => {
                    this.initializeDatePicker();
                });
            })
            .catch(errors => {
                console.error(errors);
            });

        if (this.step4Page === true) {
            new EventObject("New Car - Test Drive Form", "Revisit", "Step 4 Page").dispatch();
        } else {
            new EventObject("New Car - Test Drive Form", "Visited", "Step 4 Page").dispatch();
            this.$store.commit("setStep4Page", true);
        }
    },
    methods: {
        initializeDatePicker() {
            this.flatpickrInstance = flatpickr(this.$refs.date, {
                minDate: this.minDate,
                maxDate: this.maxDate,
                altInput: false,
                dateFormat: "D J F Y",
                locale: {
                    firstDayOfWeek: 1, // start week on Monday
                },
                disableMobile: true,
                disable: [
                    date => {
                        const closedDaysLength = this.closedDays.length;
                        if (closedDaysLength == 0) {
                            return false;
                        }
                        for (let index = 0; index < closedDaysLength; index++) {
                            if (isSameDay(this.closedDays[index], date)) {
                                return true;
                            }
                        }

                        return false;
                    },
                ],

                onChange: ([date]) => {
                    this.chosenDate = date.toDateString();
                    this.getDealerOpeningTimes();
                    this.$store.commit("setDate", { date: date.toDateString() });
                },
            });
        },

        toggleDateSelect() {
            this.flatpickrInstance.open();
        },

        getDealerOpeningTimes() {
            return new Promise((resolve, reject) => {
                axios.get('/api/dealer-opening-times', {
                    params: {
                        dealer: this.dealer.id,
                        service_type: 'Sales',
                        interval: '60 minutes',
                        date: this.chosenDate,
                    }
                })
                    .then((response) => {
                        this.dayTimes = response.data || [];

                        resolve();

                        this.$nextTick(() => {
                            this.initializeDatePicker();
                        });
                    })
                    .catch(error => {
                        console.error(error);
                        reject(error);
                    });
            });
        },

        getDealerClosedDays() {
            return new Promise((resolve, reject) => {
                axios.get('/api/dealer-closed-days', {
                    params: {
                        dealer: this.dealer.id,
                        service_type: 'Sales',
                    }
                })
                .then((response) => {
                    this.closedDays = response.data;
                    resolve();
                })
                .catch((error) => {
                    this.throwFatalError();
                    reject();
                });
            });
        },

        updateTime(time) {
            this.selectedTime = time;
            this.$store.commit("setTime", { time: time });
        },
    },
    computed: {
        ...mapState(["step4Page", 'model', 'fuelChoice', 'transmissionChoice', 'notes', 'dealer']),

        closedDaysLength() {
            return this.closedDays.length;
        },

        haveMinimumModelData() {
            if (this.fuelChoice == '') {
                return false;
            }
            if (this.transmissionChoice == '') {
                return false;
            }
            return true;
        },

        haveMinimumDealerData() {
            if (this.dealer.length == 0) {
                return false;
            }
            return true;
        },

        date: {
            get() {
                return this.$store.state.date;
            },
        },
        storeTime: {
            get() {
                return this.$store.state.time;
            },
        },
        formattedDate() {
            return this.date ? new Date(this.date).toDateString() : "";
        },
    },
};
</script>